import { useState, useEffect, useMemo, useCallback } from "react";

export function useDeviceManager(
  aspectRatio: number,
  recordingState: "toStart" | "Review" | "Recording" | "Done",
  setHasPersistedSettings: (val: boolean) => void,
  onStartOver: () => void,
  setRecorder: (recorder: any) => void
) {
  const [camera, setCamera] = useState<string | undefined>(undefined);
  const [confirmingCamera, setConfirmingCamera] = useState<string | undefined>(undefined);
  const [microphone, setMicrophone] = useState<string | undefined>(undefined);
  const [confirmingMicrophone, setConfirmingMicrophone] = useState<string | undefined>(undefined);
  const [cameraOptions, setCameraOptions] = useState<any[]>([]);
  const [microphoneOptions, setMicrophoneOptions] = useState<any[]>([]);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoError, setVideoError] = useState<boolean>(false);
  const [withPermissions, setWithPermissions] = useState<boolean | undefined>(undefined);
  const [mountingNewWebcam, setMountingNewWebcam] = useState(false);
  const [loadingWebcam, setLoadingWebcam] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const preferredCameraId = localStorage.getItem("scRecorderPreferredCamera");
  const preferredMicId = localStorage.getItem("scRecorderPreferredMic");

  const isVertical = useMemo(() => aspectRatio === 9 / 16, [aspectRatio]);

  useEffect(() => {
    const videoDevices = devices
      .filter(({ kind }) => kind === "videoinput")
      .map(element => {
        return {
          text: element.label,
          value: element.deviceId,
        };
      });

    if (videoDevices.length > 0 && !camera) {
      const defaultCam = videoDevices.find(element => element.value === preferredCameraId) || videoDevices[0];
      setCamera(`${defaultCam.value}`);
    }

    setCameraOptions(videoDevices);

    const audios = devices
      .filter(({ kind }) => kind === "audioinput")
      .map(element => {
        return {
          text: element.label,
          value: element.deviceId,
        };
      });

    setMicrophoneOptions(audios);
    if (audios.length > 0 && !microphone) {
      const defaultMic = audios.find(element => element.value === preferredMicId) || audios[0];
      setMicrophone(`${defaultMic.value}`);
    }
  }, [devices, setCamera, setMicrophone]);

  const refreshDevices = useCallback(() => {
    navigator.mediaDevices.enumerateDevices().then(setDevices);
  }, [setDevices]);

  const remountWebcam = () => {
    setMountingNewWebcam(true);
    setTimeout(() => {
      setMountingNewWebcam(false);
    }, 100);
  };

  const updateCamera = (deviceId: string, restart: boolean = false) => {
    setConfirmingCamera(undefined);
    if (deviceId) {
      localStorage.setItem("scRecorderPreferredCamera", deviceId);
      setHasPersistedSettings(true);
      if (restart) {
        onStartOver();
      } else {
        setRecorder(undefined);
        setCamera(deviceId);
        remountWebcam();
      }
    }
  };

  const confirmCamera = (deviceId: string) => {
    if (deviceId) {
      if (recordingState !== "toStart") {
        setConfirmingCamera(deviceId);
        setShowConfirmationModal(true);
      } else {
        updateCamera(deviceId);
      }
    }
  };

  const updateMic = (deviceId: string, restart: boolean = false) => {
    setConfirmingMicrophone(undefined);
    if (deviceId) {
      localStorage.setItem("scRecorderPreferredMic", deviceId);
      setHasPersistedSettings(true);
      if (restart) {
        onStartOver();
      } else {
        setRecorder(undefined);
        setMicrophone(deviceId);
        remountWebcam();
      }
    }
  };

  const confirmMic = (deviceId: string) => {
    if (deviceId) {
      if (recordingState !== "toStart") {
        setConfirmingMicrophone(deviceId);
        setShowConfirmationModal(true);
      } else {
        updateMic(deviceId);
      }
    }
  };

  const maxDeviceHeight: number | undefined = useMemo(() => {
    const device = devices.find(element => element.deviceId === camera);
    if (device && "getCapabilities" in device) {
      const capabilities = (device as unknown as MediaStreamTrack).getCapabilities();
      return capabilities?.height?.max;
    }

    return undefined;
  }, [devices, camera]);

  const resolutionWarning = useMemo(() => {
    const minHeight = 960;
    if (maxDeviceHeight && maxDeviceHeight < minHeight) {
      return `The selected camera has a resolution of ${maxDeviceHeight}px. The minimum resolution for most social networks is ${minHeight}px. Please consider using a camera with a higher resolution.`;
    }
    return undefined;
  }, [maxDeviceHeight]);

  const videoConstraints: MediaTrackConstraints | boolean = useMemo(() => {
    const baseConstraints = {
      facingMode: "user",
      frameRate: { max: 30, ideal: 30 },
      height: { ideal: isVertical ? 1920 : 1080 },
      aspectRatio: { exact: aspectRatio },
      resizeMode: "crop-and-scale",
    };

    if (!camera) return baseConstraints;

    return {
      ...baseConstraints,
      deviceId: { exact: camera },
    } as MediaTrackConstraints;
  }, [camera, isVertical, aspectRatio]);

  const audioConstraints: MediaTrackConstraints | boolean = useMemo(() => {
    if (!microphone) return true;
    return {
      deviceId: { exact: microphone },
    };
  }, [microphone]);

  return {
    camera,
    setCamera,
    confirmingCamera,
    setConfirmingCamera,
    microphone,
    setMicrophone,
    confirmingMicrophone,
    setConfirmingMicrophone,
    cameraOptions,
    microphoneOptions,
    devices,
    videoError,
    setVideoError,
    withPermissions,
    setWithPermissions,
    mountingNewWebcam,
    setMountingNewWebcam,
    loadingWebcam,
    setLoadingWebcam,
    refreshDevices,
    confirmCamera,
    confirmMic,
    updateCamera,
    updateMic,
    maxDeviceHeight,
    resolutionWarning,
    videoConstraints,
    audioConstraints,
    showConfirmationModal,
    setShowConfirmationModal,
  };
}
